import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "lib/utils";
import { getInputModality } from "../../util/inputModality";

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/50",
      "data-[state=open]:animate-in data-[state=open]:fade-in-0",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const dialogContentVariants = cva(
  // Shared: solid surface, focus ring suppressed on the container itself (focus is trapped inside)
  "z-50 bg-background text-foreground outline-none",
  {
    variants: {
      position: {
        // Centered modal card (default ShadCN behaviour)
        center:
          "fixed left-1/2 top-1/2 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-[12px] border border-foreground/10 p-6 shadow-md data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        // Full-screen slide-up panel (mobile-first booking flows)
        fullScreen:
          "fixed inset-0 mx-auto max-w-md overflow-y-auto data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom data-[state=open]:duration-300 data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=closed]:duration-200",
      },
    },
    defaultVariants: {
      position: "center",
    },
  }
);

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> &
  VariantProps<typeof dialogContentVariants> & {
    /** Render the built-in top-right close button (centered modals only). */
    showCloseButton?: boolean;
    /** Accessible label for the built-in close button. */
    closeLabel?: string;
    /** Render the dimming overlay behind the content. */
    withOverlay?: boolean;
  };

/**
 * Radix returns focus to the trigger when a dialog closes. That is right for a
 * keyboard user and wrong for a touch user, because WebKit matches
 * `:focus-visible` on PROGRAMMATIC focus — so on iOS the trigger comes back
 * wearing the 2px primary focus ring. On the booking header's round close
 * button that renders as a teal circle around the ✕ which the customer cannot
 * get rid of (PO, KGSt-Forum walkthrough, 2026-09-15).
 *
 * So: restore focus when the user is on a keyboard, skip it when they are not.
 * The ring itself is untouched and still does its job (WCAG 2.4.7); it simply
 * stops being shown to someone who never used a keyboard.
 *
 * Exported because it is the part worth testing — Radix's own focus
 * restoration does not run under jsdom, so a test asserting `document
 * .activeElement` after a close passes for the wrong reason.
 */
export const handleCloseAutoFocus = (event: Event): void => {
  if (getInputModality() !== "keyboard") event.preventDefault();
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      position,
      showCloseButton = true,
      closeLabel,
      withOverlay = true,
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      {withOverlay && <DialogOverlay />}
      <DialogPrimitive.Content
        ref={ref}
        className={cn(dialogContentVariants({ position }), className)}
        // A caller passing its own `onCloseAutoFocus` wins — the spread is
        // after this. See `handleCloseAutoFocus` above for why it exists.
        onCloseAutoFocus={handleCloseAutoFocus}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            className="absolute right-4 top-4 rounded-full p-1 text-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ring-offset-background"
          >
            <X className="size-5" />
            <span className="sr-only">{closeLabel ?? "Close"}</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-foreground/60", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogDescription,
};
